import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CreditCard, 
  Gift, 
  Star, 
  TrendingUp, 
  Send, 
  Lock, 
  Unlock,
  DollarSign,
  Calendar,
  Award,
  Package,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

const CreditsAccessDashboard = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creditSummary, setCreditSummary] = useState(null);
  const [accessStatus, setAccessStatus] = useState(null);
  const [creditPackages, setCreditPackages] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch all data on component mount
  useEffect(() => {
    fetchAllData();
  }, [token]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchCreditSummary(),
        fetchAccessStatus(),
        fetchCreditPackages(),
        fetchCreditHistory()
      ]);
    } catch (error) {
      console.error('Error fetching credits data:', error);
      toast.error('Failed to load credits information');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditSummary = async () => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/credits/summary`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      setCreditSummary(data);
    }
  };

  const fetchAccessStatus = async () => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/access/status`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      setAccessStatus(data);
    }
  };

  const fetchCreditPackages = async () => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/credits/packages`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      setCreditPackages(data.packages || []);
    }
  };

  const fetchCreditHistory = async () => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/credits/history?per_page=10`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      setCreditHistory(data.transactions || []);
    }
  };

  const handleUnlockFeature = async (featureId, featureName) => {
    if (!window.confirm(`Unlock "${featureName}"? This will deduct credits from your balance.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/features/unlock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          feature_id: featureId,
          confirm_spending: true
        })
      });

      if (response.ok) {
        toast.success(`Successfully unlocked ${featureName}!`);
        fetchAllData(); // Refresh data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to unlock feature');
      }
    } catch (error) {
      console.error('Error unlocking feature:', error);
      toast.error('Failed to unlock feature');
    }
  };

  const getTierBadgeColor = (tier) => {
    const colors = {
      free: 'bg-gray-100 text-gray-800',
      boot_camp: 'bg-blue-100 text-blue-800',
      rookie_pro: 'bg-green-100 text-green-800',
      pro: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-orange-100 text-orange-800',
      vip: 'bg-red-100 text-red-800'
    };
    return colors[tier] || colors.free;
  };

  const formatCredits = (amount) => {
    return new Intl.NumberFormat().format(amount);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Credits & Access</h1>
        <p className="text-gray-600">Manage your credits and unlock premium features</p>
      </div>

      {/* Credit Summary Cards */}
      {creditSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Available Credits</p>
                <p className="text-3xl font-bold">{formatCredits(creditSummary.available_credits)}</p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-200" />
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Earned</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCredits(creditSummary.lifetime_earned)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Spent</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCredits(creditSummary.lifetime_spent)}</p>
              </div>
              <Send className="h-8 w-8 text-orange-500" />
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Current Tier</p>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getTierBadgeColor(creditSummary.current_tier)}`}>
                  {creditSummary.current_tier.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <Star className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('features')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'features'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Unlock Features
          </button>
          <button
            onClick={() => setActiveTab('packages')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'packages'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Buy Credits
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && accessStatus && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Available Features */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Unlock className="h-5 w-5 text-green-500 mr-2" />
              Available Features ({accessStatus.available_features?.length || 0})
            </h3>
            <div className="space-y-3">
              {accessStatus.available_features?.slice(0, 5).map((feature, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div>
                    <p className="font-medium text-green-900">{feature.name}</p>
                    <p className="text-sm text-green-700">{feature.description}</p>
                  </div>
                  <Unlock className="h-4 w-4 text-green-500" />
                </div>
              ))}
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 text-blue-500 mr-2" />
              Recent Transactions
            </h3>
            <div className="space-y-3">
              {creditHistory.slice(0, 5).map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{transaction.description}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(transaction.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`font-semibold ${
                    transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.amount > 0 ? '+' : ''}{formatCredits(transaction.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'features' && accessStatus && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {accessStatus.locked_features?.map((feature) => (
            <div key={feature.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <Lock className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-500 uppercase tracking-wide">
                    {feature.category}
                  </span>
                </div>
                {feature.credit_cost && (
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                    {formatCredits(feature.credit_cost)} credits
                  </span>
                )}
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.name}</h3>
              <p className="text-gray-600 mb-4 text-sm">{feature.description}</p>
              
              {feature.credit_cost ? (
                <button
                  onClick={() => handleUnlockFeature(feature.id, feature.name)}
                  disabled={!creditSummary || creditSummary.available_credits < feature.credit_cost}
                  className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                    creditSummary && creditSummary.available_credits >= feature.credit_cost
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {creditSummary && creditSummary.available_credits >= feature.credit_cost 
                    ? 'Unlock Feature' 
                    : 'Insufficient Credits'
                  }
                </button>
              ) : (
                <div className="w-full py-2 px-4 rounded-lg bg-yellow-100 text-yellow-800 text-center font-medium">
                  Requires {feature.required_tier?.replace('_', ' ').toUpperCase()} tier
                </div>
              )}
            </div>
          ))}
          
          {(!accessStatus.locked_features || accessStatus.locked_features.length === 0) && (
            <div className="col-span-full text-center py-8">
              <Award className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900">All Features Unlocked!</p>
              <p className="text-gray-600">You have access to all available features.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'packages' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {creditPackages.map((pkg) => (
            <div key={pkg.id} className={`border-2 rounded-lg p-6 ${
              pkg.is_promotional ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-white'
            }`}>
              {pkg.is_promotional && (
                <div className="bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-medium mb-4 inline-block">
                  LIMITED TIME
                </div>
              )}
              
              <div className="text-center mb-6">
                <Package className="h-12 w-12 text-blue-500 mx-auto mb-2" />
                <h3 className="text-xl font-bold text-gray-900">{pkg.name}</h3>
                <p className="text-gray-600 mt-2">{pkg.description}</p>
              </div>
              
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-gray-900">{formatCurrency(pkg.price)}</div>
                <div className="text-lg text-gray-600">
                  {formatCredits(pkg.credit_amount)} credits
                  {pkg.bonus_credits > 0 && (
                    <span className="text-green-600"> + {formatCredits(pkg.bonus_credits)} bonus</span>
                  )}
                </div>
              </div>
              
              <button className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center">
                <DollarSign className="h-4 w-4 mr-2" />
                Purchase Package
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {creditHistory.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{transaction.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        transaction.transaction_type === 'earned' ? 'bg-green-100 text-green-800' :
                        transaction.transaction_type === 'spent' ? 'bg-red-100 text-red-800' :
                        transaction.transaction_type === 'bonus' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {transaction.transaction_type.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${
                        transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}{formatCredits(transaction.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(transaction.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditsAccessDashboard;